/*
 * Solbiome Recharge Widget Enhancements
 *
 * Enhances subscription options only.
 * The Recharge one-time purchase option remains unchanged.
 */

(function () {
  'use strict';

  const CONFIG = {
    enableFreeGift: window.solbiomeRechargeSettings?.enableFreeGift || false,
    giftProductHandle:
    window.solbiomeRechargeSettings?.giftProductHandle || 'sol-mates-puffer-bag',
    giftPropertyName: '_solbiome_free_gift',
    giftPropertyValue: '3-month-subscription',

    colors: {
      accent: '#FF4712',
      text: '#121212',
      muted: '#686868',
      softBackground: '#FFF4F0',
      unselectedBorder: '#D6D6D6',
      selectedBackground: '#FFFFFF'
    }
  };

  let rechargeObserver = null;
  let addToCartObserver = null;
  let updateTimer = null;
  let giftVariantId = null;
  let giftProductTitle = null;
  let giftProductData = null;
  let giftReconcileTimer = null;
  let giftReconcileRunning = false;
  let originalFetch = null;
  let rechargeCartControlsTimer = null;

  function getNumber(value) {
    if (typeof value === 'number') return value;

    return (
      parseFloat(
        String(value || '')
          .replace(/,/g, '')
          .replace(/[^0-9.-]/g, '')
      ) || 0
    );
  }

  function getCurrencySymbol(value) {
    const match = String(value || '').match(/[^0-9.,\s-]+/);
    return match ? match[0] : '$';
  }

  function formatMoney(amount, referencePrice) {
    const symbol = getCurrencySymbol(referencePrice);

    return (
      symbol +
      Number(amount || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    );
  }

  function setText(element, value) {
    if (element && element.textContent !== value) {
      element.textContent = value;
    }
  }

  function getRechargeProductId() {
    return Number(
      document
        .querySelector('recharge-subscription-widget')
        ?.getAttribute('product-id') || 0
    );
  }

  async function getGiftVariantId() {
    if (giftVariantId && giftProductData) return giftVariantId;

    const response = await originalFetch(
      `/products/${CONFIG.giftProductHandle}.js`,
      {
        credentials: 'same-origin'
      }
    );

    if (!response.ok) return null;

    const product = await response.json();
    const availableVariant =
      product.variants?.find((variant) => variant.available) ||
      product.variants?.[0];

    giftProductData = product;
    giftProductTitle = product.title || null;
    giftVariantId = availableVariant?.id || null;
    return giftVariantId;
  }

  function hideRechargeFreeGiftControls() {
    const cartDrawer = document.querySelector(
      '#recharge-storefront-experiences__cart-drawer'
    );
    const root = cartDrawer?.shadowRoot;

    if (!root || !giftProductTitle) return;

    root.querySelectorAll('[role="listitem"]').forEach((lineItem) => {
      const title = lineItem.querySelector('p.font-semibold')?.textContent.trim();
      const hasFreePrice = Array.from(
        lineItem.querySelectorAll('[part~="discount"]')
      ).some((element) => getNumber(element.textContent) === 0);

      if (title !== giftProductTitle || !hasFreePrice) return;

      const quantityInput = lineItem.querySelector('input[name="quantity"]');
      const quantityControls = quantityInput?.parentElement;
      const removeButton = Array.from(
        lineItem.querySelectorAll('button')
      ).find((button) =>
        button
          .querySelector('.sr-only')
          ?.textContent.trim()
          .toLowerCase()
          .startsWith('remove ')
      );

      if (quantityControls) {
        quantityControls.style.setProperty('display', 'none', 'important');
      }

      if (removeButton) {
        removeButton.style.setProperty('display', 'none', 'important');
      }
    });
  }

  function watchRechargeFreeGiftControls() {
    if (!CONFIG.enableFreeGift) return;
    if (rechargeCartControlsTimer) return;

    rechargeCartControlsTimer = window.setInterval(
      hideRechargeFreeGiftControls,
      500
    );
  }

  function isQualifyingSubscriptionLine(line, rechargeProductId) {
    return (
      Number(line.product_id) === rechargeProductId &&
      Boolean(line.selling_plan_allocation) &&
      Number(line.quantity) >= 3
    );
  }

  function isAutoAddedGiftLine(line) {
    return (
      line.properties?.[CONFIG.giftPropertyName] === CONFIG.giftPropertyValue
    );
  }

  function notifyCartChanged(cart) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          cart
        }
      })
    );

    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: {
          cart
        }
      })
    );
  }

  async function reconcileFreeGift() {
    if (giftReconcileRunning || !originalFetch) return;

    const rechargeProductId = getRechargeProductId();
    if (!rechargeProductId) return;

    giftReconcileRunning = true;

    try {
      const cartResponse = await originalFetch('/cart.js', {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      });

      if (!cartResponse.ok) return;

      const cart = await cartResponse.json();
      const qualifies = cart.items.some((line) =>
        isQualifyingSubscriptionLine(line, rechargeProductId)
      );
      const autoGiftLines = cart.items.filter(isAutoAddedGiftLine);

      if (autoGiftLines[0]?.product_title) {
        giftProductTitle = autoGiftLines[0].product_title;
      }

      if (qualifies && autoGiftLines.length === 0) {
        const variantId = await getGiftVariantId();
        if (!variantId) return;

        const addResponse = await originalFetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                properties: {
                  [CONFIG.giftPropertyName]: CONFIG.giftPropertyValue
                }
              }
            ]
          })
        });

        if (!addResponse.ok) return;
      }

      if (qualifies && autoGiftLines.length > 1) {
        for (const duplicateGift of autoGiftLines.slice(1)) {
          await originalFetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              id: duplicateGift.key,
              quantity: 0
            })
          });
        }
      }

      if (!qualifies && autoGiftLines.length > 0) {
        for (const autoGift of autoGiftLines) {
          await originalFetch('/cart/change.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              id: autoGift.key,
              quantity: 0
            })
          });
        }
      }

      if (
        (qualifies && autoGiftLines.length !== 1) ||
        (!qualifies && autoGiftLines.length > 0)
      ) {
        const updatedCartResponse = await originalFetch('/cart.js', {
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          }
        });

        if (updatedCartResponse.ok) {
          notifyCartChanged(await updatedCartResponse.json());
        }
      }
    } catch (error) {
      console.warn('Solbiome free gift could not be synchronized.', error);
    } finally {
      giftReconcileRunning = false;
    }
  }

  function scheduleGiftReconcile(delay = 350) {
    window.clearTimeout(giftReconcileTimer);
    giftReconcileTimer = window.setTimeout(reconcileFreeGift, delay);
  }

  function watchCartRequests() {
    if (!CONFIG.enableFreeGift) return;
    if (originalFetch || !window.fetch) return;

    originalFetch = window.fetch.bind(window);

    window.fetch = async function (...args) {
      const response = await originalFetch(...args);
      const requestUrl =
        typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

      if (
        /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(requestUrl)
      ) {
        scheduleGiftReconcile();
      }

      return response;
    };

    scheduleGiftReconcile(700);
  }

  function injectAddToCartStyles() {
    if (document.querySelector('#solbiome-add-to-cart-price-styles')) return;

    const style = document.createElement('style');
    style.id = 'solbiome-add-to-cart-price-styles';
    style.textContent = `
      .solbiome-atc-content {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: 7px;
      }

      .solbiome-atc-separator {
        opacity: 0.7;
      }

      .solbiome-atc-price {
        font-weight: 700;
      }

      .solbiome-atc-compare-price {
        opacity: 0.65;
        text-decoration: line-through;
      }

      body.solbiome-recharge-active
        product-quick-add
        .product-quick-add__variant
        price-list {
        display: none !important;
      }

      body.solbiome-recharge-active
        sticky-buy-button
        .wt-product__sticky-buy__product-info
        [id^="sticky-price-"] {
        display: none !important;
      }
    `;

    document.head.appendChild(style);
  }

  function getWidgetData() {
    const widget = document.querySelector('recharge-subscription-widget');
    if (!widget?.shadowRoot) return null;

    const upsell = widget.shadowRoot.querySelector('recharge-quantity-upsell');
    if (!upsell?.shadowRoot) return null;

    const formattedPrices =
      window.SubscriptionWidgetConfig?.formattedPrices || {};

    const variantInput = document.querySelector(
      'form[action*="/cart/add"] [name="id"]'
    );

    const variantId =
      variantInput?.value || widget.getAttribute('default-variant-id');

    const priceData =
      formattedPrices[variantId] ||
      formattedPrices[String(variantId)] ||
      Object.values(formattedPrices)[0];

    if (!priceData) return null;

    return {
      root: upsell.shadowRoot,
      priceData
    };
  }

  function injectStyles(root) {
    if (root.querySelector('#solbiome-recharge-styles')) return;

    const style = document.createElement('style');
    style.id = 'solbiome-recharge-styles';
    style.textContent = `
      .rc-quantity-upsell-option {
        position: relative !important;
        border: 1px solid ${CONFIG.colors.unselectedBorder} !important;
        border-radius: 14px !important;
        overflow: visible !important;
        transition:
          border-color 0.2s ease,
          box-shadow 0.2s ease,
          background-color 0.2s ease !important;
      }

      .rc-quantity-upsell-option[rc-selected] {
        border: 2px solid ${CONFIG.colors.accent} !important;
        background: ${CONFIG.colors.selectedBackground} !important;
        box-shadow: 0 4px 16px rgba(255, 71, 18, 0.10) !important;
      }

      .rc-quantity-upsell-option__selector {
        align-items: flex-start !important;
      }

      .rc-quantity-upsell-option__header {
        align-items: flex-start !important;
        gap: 16px !important;
      }

      .rc-quantity-upsell-option__title {
        color: ${CONFIG.colors.text} !important;
        line-height: normal !important;
      }

      .solbiome-native-price {
        display: none !important;
      }

      .solbiome-price-breakdown {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        flex-shrink: 0;
        gap: 3px;
        color: ${CONFIG.colors.text};
        text-align: right;
        white-space: nowrap;
      }

      .solbiome-save-amount {
        display: inline-flex;
        align-items: center;
        width: fit-content;
        margin-bottom: 3px;
        padding: 5px 9px;
        border-radius: 7px;
        background: ${CONFIG.colors.softBackground};
        color: ${CONFIG.colors.accent};
        font-size: 13px;
        font-weight: 700;
        line-height: 1.1;
      }

      .rc-quantity-upsell-option[rc-selected] .solbiome-save-amount {
        background: ${CONFIG.colors.accent};
        color: #FFFFFF;
      }

      .solbiome-per-month {
        font-size: 16px;
        font-weight: 700;
        line-height: 1.25;
      }

      .solbiome-per-day {
        color: ${CONFIG.colors.muted};
        font-size: 13px;
        font-weight: 500;
        line-height: 1.25;
      }

      .solbiome-total-row {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        align-items: flex-end;
        gap: 3px;
        margin-top: 3px;
        font-size: 12px;
        line-height: 1.2;
      }

      .solbiome-total-price {
        font-weight: 600;
      }

      .solbiome-box-price-row {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 6px;
      }

      .solbiome-compare-price {
        color: #999999;
        text-decoration: line-through;
      }

      .rc-purchase-option__plan-discount-badge {
        display: none !important;
      }

      .rc-quantity-upsell-option[rc-selected]
        .rc-purchase-option__plan-discount-badge {
        display: none !important;
      }

      .rc-purchase-option__best-value-badge,
      .rc-purchase-option__recommended-badge,
      .solbiome-best-value-badge {
        position: absolute !important;
        top: 0 !important;
        left: 50% !important;
        right: auto !important;
        transform: translate(-50%, -50%) !important;
        background: ${CONFIG.colors.accent} !important;
        border-color: ${CONFIG.colors.accent} !important;
        color: #FFFFFF !important;
        border-radius: 7px !important;
        font-weight: 700 !important;
        padding: 2px 5px !important;
      }

      .solbiome-free-gift {
        margin-top: 12px;
        padding: 12px;
        border-top: 1px solid ${CONFIG.colors.unselectedBorder};
        border-radius: 8px;
        background: #fff5ef;
        color: ${CONFIG.colors.text};
      }

      .solbiome-free-gift__teaser {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        line-height: 1.3;
      }

      .solbiome-free-gift__teaser-image {
        width: 32px;
        height: 32px;
        flex: 0 0 32px;
        border-radius: 5px;
        object-fit: cover;
      }

      .solbiome-free-gift__full {
        display: none;
        align-items: center;
        gap: 12px;
      }

      .solbiome-free-gift__image {
        width: 72px;
        height: 72px;
        flex: 0 0 72px;
        border-radius: 8px;
        object-fit: cover;
      }

      .solbiome-free-gift__body {
        display: flex;
        flex-direction: column;
        gap: 5px;
      }

      .solbiome-free-gift__title {
        font-size: 13px;
        line-height: 1.3;
      }

      .solbiome-free-gift__prices {
        display: flex;
        align-items: center;
        gap: 7px;
        font-size: 14px;
        line-height: 1.2;
      }

      .solbiome-free-gift__free-price {
        color: ${CONFIG.colors.accent};
        font-weight: 700;
      }

      .solbiome-free-gift__compare-price {
        color: #999999;
        text-decoration: line-through;
      }

      .rc-quantity-upsell-option[rc-selected]
        .solbiome-free-gift__teaser {
        display: none;
      }

      .rc-quantity-upsell-option[rc-selected]
        .solbiome-free-gift__full {
        display: flex;
      }

      .rc-quantity-upsell-option[rc-selected]
        .solbiome-free-gift {
        background: #FF4712;
        border-color: #FF4712;
        color: #FFFFFF;
      }

      .rc-quantity-upsell-option[rc-selected]
        .solbiome-free-gift__free-price {
        color: #FFFFFF;
      }

      .rc-quantity-upsell-option[rc-selected]
        .solbiome-free-gift__compare-price {
        color: rgba(255, 255, 255, 0.75);
      }

      @media (max-width: 600px) {
        .rc-quantity-upsell-option__header {
          gap: 10px !important;
        }

        .solbiome-per-month {
          font-size: 14px;
        }

        .solbiome-total-row {
          font-size: 11px;
        }

        .solbiome-save-amount {
          padding: 4px 7px;
          font-size: 11px;
        }

        .solbiome-free-gift__image {
          width: 62px;
          height: 62px;
          flex-basis: 62px;
        }
      }
    `;

    root.appendChild(style);
  }

  function createBreakdown(header) {
    const breakdown = document.createElement('div');
    breakdown.className = 'solbiome-price-breakdown';
    breakdown.innerHTML = `
      <span class="solbiome-save-amount"></span>
      <span class="solbiome-total-row">
        <span class="solbiome-total-price"></span>
      </span>
      <span class="solbiome-box-price-row">
        <span class="solbiome-per-month"></span>
        <span class="solbiome-compare-price"></span>
      </span>
    `;

    header.appendChild(breakdown);
    return breakdown;
  }

  function restoreOneTimeOption(option, header) {
    header.querySelector('.solbiome-price-breakdown')?.remove();

    header.querySelectorAll('.solbiome-native-price').forEach((element) => {
      element.classList.remove('solbiome-native-price');
    });

    option.classList.remove('solbiome-subscription-option');

    const drawbacks = option.querySelector('rc-onetime-drawbacks');
    const drawbacksList = drawbacks?.shadowRoot?.querySelector(
      '.rc-benefits__list'
    );

    if (drawbacksList) {
      drawbacksList.style.setProperty('margin-left', '-25px', 'important');
    }
  }

  function tightenSubscriptionContent(option, plan) {
    const benefits = option.querySelector('rc-benefits');

    if (benefits?.shadowRoot) {
      const benefitsList = benefits.shadowRoot.querySelector(
        '.rc-benefits__list'
      );

      if (benefitsList) {
        benefitsList.style.setProperty('margin-top', '-25px', 'important');
        benefitsList.style.setProperty('margin-left', '-25px', 'important');

        benefitsList.querySelectorAll('p').forEach((paragraph) => {
          paragraph.style.setProperty('margin-top', '0', 'important');
          paragraph.style.setProperty('margin-bottom', '2px', 'important');
        });

        const benefitsHeading = benefitsList.querySelector('p strong');

        if (benefitsHeading && plan?.discountValue != null) {
          setText(
            benefitsHeading,
            `FREE SHIPPING + SAVE ${plan.discountValue}%`
          );
        }
      }

    }

    const learnMore = option.querySelector('rc-learn-more');

    if (learnMore?.shadowRoot) {
      const learnMoreWrapper = learnMore.shadowRoot.querySelector(
        '.rc-learn-more'
      );

      if (learnMoreWrapper) {
        learnMoreWrapper.style.setProperty('margin-top', '-15px', 'important');
      }
    }
  }

  function isThreeMonthPlan(plan, priceData) {
    const planUnitPrice = getNumber(plan?.price);
    const totalPrice = getNumber(plan?.quantityUpsell?.totalDiscountedPrice);

    if (!planUnitPrice || !totalPrice) return false;

    return Math.round(totalPrice / planUnitPrice) === 3;
  }

  function ensureThreeMonthBestValueBadge(root, priceData) {
    const options = Array.from(
      root.querySelectorAll('.rc-quantity-upsell-option')
    );

    const threeMonthOption = options.find((option) => {
      const planId = String(
        option.querySelector('input[name="quantityUpsellOption"]')?.value || ''
      );
      const plan = priceData.sellingPlans?.[planId];

      return plan && isThreeMonthPlan(plan, priceData);
    });

    if (!threeMonthOption) return;

    const badgeSelector =
      '.rc-purchase-option__best-value-badge, ' +
      '.rc-purchase-option__recommended-badge, ' +
      '.solbiome-best-value-badge';

    options.forEach((option) => {
      if (option === threeMonthOption) return;
      option.querySelectorAll(badgeSelector).forEach((badge) => badge.remove());
    });

    let badge = threeMonthOption.querySelector(badgeSelector);

    if (!badge) {
      badge = document.createElement('span');
      badge.setAttribute('aria-hidden', 'true');
      threeMonthOption.prepend(badge);
    }

    badge.className = 'solbiome-best-value-badge';
    setText(badge, 'Best value');
  }

  function createGiftPanel() {
    const panel = document.createElement('div');
    panel.className = 'solbiome-free-gift';

    panel.innerHTML = `
      <div class="solbiome-free-gift__teaser">
        <img class="solbiome-free-gift__teaser-image" alt="">
        <span class="solbiome-free-gift__teaser-text"></span>
      </div>
      <div class="solbiome-free-gift__full">
        <img class="solbiome-free-gift__image" alt="">
        <div class="solbiome-free-gift__body">
          <div class="solbiome-free-gift__title"></div>
          <div class="solbiome-free-gift__prices">
            <span class="solbiome-free-gift__free-price">$0.00</span>
            <span class="solbiome-free-gift__compare-price"></span>
          </div>
        </div>
      </div>
    `;

    return panel;
  }

  async function updateGiftPanel(option, contentContainer) {
    let panel = contentContainer.querySelector('.solbiome-free-gift');

    if (!panel) {
      panel = createGiftPanel();
      contentContainer.appendChild(panel);
    }

    if (!giftProductData) {
      await getGiftVariantId();
    }

    if (!giftProductData) return;

    const variant =
      giftProductData.variants?.find((item) => item.available) ||
      giftProductData.variants?.[0];
    const title = giftProductData.title || 'Free gift';
    const image =
      variant?.featured_image?.src ||
      giftProductData.featured_image ||
      giftProductData.images?.[0] ||
      '';
    const price = getNumber(variant?.price) / 100;
    const formattedPrice = formatMoney(price, '$');

    panel.querySelectorAll('img').forEach((imageElement) => {
      imageElement.src = image;
      imageElement.alt = title;
    });

    panel.querySelector('.solbiome-free-gift__teaser-text').innerHTML =
      `<strong>+ FREE GIFT:</strong> ${title}`;
    panel.querySelector('.solbiome-free-gift__title').innerHTML =
      `<strong>FREE ${title}</strong>`;
    setText(panel.querySelector('.solbiome-free-gift__compare-price'), formattedPrice);
  }

  function getSelectedPriceData(root, priceData) {
    const selectedInput = root.querySelector(
      '.rc-quantity-upsell-option[rc-selected] input[name="quantityUpsellOption"]'
    );

    if (!selectedInput) return null;

    const planId = String(selectedInput.value || '');
    const plan = priceData.sellingPlans?.[planId];
    const regularUnitPrice = getNumber(priceData.unit);

    if (!plan) {
      return {
        price: regularUnitPrice,
        comparePrice: 0
      };
    }

    const planUnitPrice = getNumber(plan.price);
    const price =
      getNumber(plan.quantityUpsell?.totalDiscountedPrice) || planUnitPrice;
    const quantity = Math.max(1, Math.round(price / planUnitPrice));

    return {
      price,
      comparePrice: regularUnitPrice * quantity
    };
  }

  function updateAddToCartButtons(root, priceData) {
    const selectedPriceData = getSelectedPriceData(root, priceData);
    if (!selectedPriceData) return;

    injectAddToCartStyles();

    document
      .querySelectorAll(
        '.product-info__buy-buttons buy-buttons button[type="submit"], ' +
          'product-quick-add buy-buttons button[type="submit"], ' +
          'product-form.wt-product__add-to-cart button.js-add-to-cart[type="submit"], ' +
          'sticky-buy-button button.js-add-to-cart[type="submit"]'
      )
      .forEach((button) => {
        const content =
          button.querySelector(
            ':scope > span:not(.button__loader):not(.loading-overlay__spinner)'
          ) ||
          button.querySelector(':scope > div:not(.loading-overlay__spinner)');

        if (!content) return;

        button
          .querySelectorAll(
            ':scope > .loading-overlay__spinner.solbiome-atc-content'
          )
          .forEach((loader) => {
            loader.classList.remove('solbiome-atc-content');
            loader.removeAttribute('data-solbiome-original-label');
            loader
              .querySelectorAll(
                '.solbiome-atc-label, .solbiome-atc-separator, .solbiome-atc-price, .solbiome-atc-compare-price'
              )
              .forEach((element) => element.remove());
          });

        if (!content.dataset.solbiomeOriginalLabel) {
          content.dataset.solbiomeOriginalLabel =
            content.textContent.trim() || 'Add to cart';
        }

        content.classList.add('solbiome-atc-content');

        let label = content.querySelector('.solbiome-atc-label');
        let separator = content.querySelector('.solbiome-atc-separator');
        let price = content.querySelector('.solbiome-atc-price');
        let comparePrice = content.querySelector(
          '.solbiome-atc-compare-price'
        );

        if (!label) {
          content.textContent = '';

          label = document.createElement('span');
          label.className = 'solbiome-atc-label';

          separator = document.createElement('span');
          separator.className = 'solbiome-atc-separator';
          separator.textContent = '|';

          price = document.createElement('span');
          price.className = 'solbiome-atc-price';

          comparePrice = document.createElement('span');
          comparePrice.className = 'solbiome-atc-compare-price';

          content.append(label, separator, price, comparePrice);
        }

        setText(label, content.dataset.solbiomeOriginalLabel);
        setText(
          price,
          formatMoney(selectedPriceData.price, priceData.unit)
        );

        if (selectedPriceData.comparePrice > selectedPriceData.price) {
          comparePrice.hidden = false;
          setText(
            comparePrice,
            formatMoney(selectedPriceData.comparePrice, priceData.unit)
          );
        } else {
          comparePrice.hidden = true;
          setText(comparePrice, '');
        }
      });
  }

  function enhanceOption(option, priceData) {
    const input = option.querySelector('input[name="quantityUpsellOption"]');
    const header = option.querySelector('.rc-quantity-upsell-option__header');

    if (!input || !header) return;

    const planId = String(input.value || '');
    const plan = priceData.sellingPlans?.[planId];
    const contentContainer = option.querySelector(
      '.rc-quantity-upsell-option__content-container'
    );

    // Keep Recharge's one-time purchase option completely unchanged.
    if (!plan) {
      restoreOneTimeOption(option, header);
      return;
    }

    option.classList.add('solbiome-subscription-option');

    if (contentContainer) {
      if (CONFIG.enableFreeGift && isThreeMonthPlan(plan, priceData)) {
        updateGiftPanel(option, contentContainer);
        } else {
        contentContainer.querySelector('.solbiome-free-gift')?.remove();
      }
    }

    const nativePrice = Array.from(header.children).find(
      (element) =>
        element !== header.firstElementChild &&
        !element.classList.contains('solbiome-price-breakdown')
    );

    if (nativePrice) {
      nativePrice.classList.add('solbiome-native-price');
    }

    let breakdown = header.querySelector('.solbiome-price-breakdown');

    if (!breakdown) {
      breakdown = createBreakdown(header);
    }

    const regularUnitPrice = getNumber(priceData.unit);
    const planUnitPrice = getNumber(plan.price);
    const totalPrice =
      getNumber(plan.quantityUpsell?.totalDiscountedPrice) || planUnitPrice;

    const quantity = Math.max(1, Math.round(totalPrice / planUnitPrice));
    const regularTotal = regularUnitPrice * quantity;
    const perMonthPrice = totalPrice / quantity;

    const discountAmount =
      getNumber(plan.quantityUpsell?.totalDiscountAmount) ||
      Math.max(0, regularTotal - totalPrice);

    const saveElement = breakdown.querySelector('.solbiome-save-amount');
    const perMonthElement = breakdown.querySelector('.solbiome-per-month');
    const totalElement = breakdown.querySelector('.solbiome-total-price');
    const compareElement = breakdown.querySelector('.solbiome-compare-price');

    saveElement.hidden = discountAmount <= 0;
    setText(saveElement, `Save ${formatMoney(discountAmount, priceData.unit)}`);
    setText(
      perMonthElement,
      `${formatMoney(perMonthPrice, priceData.unit)}/box`
    );
    setText(totalElement, `${formatMoney(totalPrice, priceData.unit)} total`);

    if (regularTotal > totalPrice) {
      compareElement.hidden = false;
      setText(compareElement, formatMoney(regularUnitPrice, priceData.unit));
    } else {
      compareElement.hidden = true;
      setText(compareElement, '');
    }

    tightenSubscriptionContent(option, plan);
  }

  function updateWidget() {
    const widgetData = getWidgetData();
    if (!widgetData) return false;

    document.body?.classList.add('solbiome-recharge-active');
    if (CONFIG.enableFreeGift) {
        watchCartRequests();
    }
    injectStyles(widgetData.root);

    widgetData.root
      .querySelectorAll('.rc-quantity-upsell-option')
      .forEach((option) => enhanceOption(option, widgetData.priceData));

    ensureThreeMonthBestValueBadge(widgetData.root, widgetData.priceData);
    updateAddToCartButtons(widgetData.root, widgetData.priceData);
    observeRecharge(widgetData.root);
    return true;
  }

  function scheduleUpdate() {
    window.clearTimeout(updateTimer);
    updateTimer = window.setTimeout(updateWidget, 10);
  }

  function observeRecharge(root) {
    if (rechargeObserver?.solbiomeRoot === root) return;

    rechargeObserver?.disconnect();
    rechargeObserver = new MutationObserver(scheduleUpdate);
    rechargeObserver.solbiomeRoot = root;

    rechargeObserver.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true
    });
  }

  function observeAddToCartButtons() {
    if (addToCartObserver || !document.body) return;

    addToCartObserver = new MutationObserver((mutations) => {
      const hasNewAddToCartContent = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some((node) => {
          if (!(node instanceof Element)) return false;

          return (
            node.matches(
              'product-quick-add, buy-buttons, product-form, sticky-buy-button, button[type="submit"]'
            ) ||
            node.querySelector(
              'product-quick-add, buy-buttons, product-form, sticky-buy-button, button[type="submit"]'
            )
          );
        })
      );

      if (hasNewAddToCartContent) {
        scheduleUpdate();
      }
    });

    addToCartObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function initialize() {
    let attempts = 0;

    const initializationTimer = window.setInterval(() => {
      attempts += 1;

      if (updateWidget()) {
        observeAddToCartButtons();
        if (CONFIG.enableFreeGift) {
            watchRechargeFreeGiftControls();
        }
        window.clearInterval(initializationTimer);
      } else if (attempts >= 50) {
        window.clearInterval(initializationTimer);
      }
    }, 50);
  }

  document.addEventListener('DOMContentLoaded', initialize);

  document.addEventListener('change', function (event) {
    if (
      event.target.matches(
        'input[name="quantityUpsellOption"], form[action*="/cart/add"] [name="id"]'
      )
    ) {
      scheduleUpdate();
    }
  });

  document.addEventListener('shopify:section:load', initialize);
})();
